import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type CleaningTeam } from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { MutedCrewsNotice } from '@/components/cleaning/muted-crews-notice'

/** A 24-hour HH:MM, matching admin-api's own validation so a bad value is caught here. */
const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d$/

/** Lowercase, url-safe, permanent: admin-api rejects anything else, and every
 *  job's `team_id` and every property's rates map keys on it forever. */
const TEAM_ID_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/

interface NewTeamDraft {
  id: string
  name: string
  short_name: string
  accounting_name: string
  emails: string
  phone: string
  color: string
  label_mode: 'nickname' | 'address'
}

const EMPTY_NEW_TEAM: NewTeamDraft = {
  id: '',
  name: '',
  short_name: '',
  accounting_name: '',
  emails: '',
  phone: '',
  color: '#C8E6C9',
  label_mode: 'nickname',
}

interface TeamDraft {
  short_name: string
  accounting_name: string
  emails: string
  phone: string
  color: string
  label_mode: 'nickname' | 'address'
  active: boolean
}

function draftFromTeam(team: CleaningTeam): TeamDraft {
  return {
    short_name: team.short_name,
    accounting_name: team.accounting_name,
    emails: team.emails.join(', '),
    phone: team.phone ?? '',
    color: team.color,
    label_mode: team.label_mode,
    active: team.active,
  }
}

/**
 * Cleaning crews and the cleaning tracker's global settings.
 *
 * Three things are deliberately shaped the way they are:
 *
 * 1. **There is no delete.** A crew is retired with `active: false`, because it
 *    stays the payee on every historical job and payable it was assigned to.
 * 2. **The tracker switch is status, not a control.** It is a wrangler var, so
 *    it changes only on a deploy - rendering it as a toggle would be a control
 *    with nothing behind it. There is no sends switch (CAP-533): who a cleaning
 *    email reaches is the crew's address, which the parallel-run notice reports.
 * 3. **Payee name is shown as its own field**, separate from the crew's name.
 *    Clearing keys on it, and the two are genuinely different strings ("Elisa
 *    (State Cleaning Solutions)" is paid as "Elisa Cleaning").
 *
 * Per-property rates are edited on each property's Cleaning card; this card
 * only reports which properties are missing one, because an enabled property
 * with no crew or no rate fails silently everywhere else.
 */
export function CleaningTeamsCard() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState<TeamDraft | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [checkinTime, setCheckinTime] = useState('')
  const [checkoutTime, setCheckoutTime] = useState('')
  const [newTeam, setNewTeam] = useState<NewTeamDraft | null>(null)

  const teamsQuery = useQuery({
    queryKey: ['cleaning-teams'],
    queryFn: () => api.getCleaningTeams(),
    staleTime: 30000,
  })

  const settingsQuery = useQuery({
    queryKey: ['cleaning-settings'],
    queryFn: () => api.getCleaningSettings(),
    staleTime: 30000,
  })

  /**
   * Re-seeded whenever fresh settings arrive, unless the operator has touched
   * the time inputs.
   *
   * Seeding only once is the obvious version and is wrong: react-query serves
   * the cached value on mount and refetches behind it, so a card that ignores
   * every response after the first shows a stale time and then writes it back
   * over the newer one on save.
   */
  const timesDirty = useRef(false)
  useEffect(() => {
    if (!settingsQuery.data || timesDirty.current) return
    setCheckinTime(settingsQuery.data.settings.checkin_time)
    setCheckoutTime(settingsQuery.data.settings.checkout_time)
  }, [settingsQuery.data])

  const teams = teamsQuery.data?.teams ?? []
  const switches = settingsQuery.data?.switches
  const timesChanged =
    !!settingsQuery.data &&
    (checkinTime !== settingsQuery.data.settings.checkin_time ||
      checkoutTime !== settingsQuery.data.settings.checkout_time)
  const timesInvalid = !TIME_OF_DAY.test(checkinTime) || !TIME_OF_DAY.test(checkoutTime)

  const startEdit = (team: CleaningTeam) => {
    setEditingId(team.id)
    setDraft(draftFromTeam(team))
  }

  const handleSaveTeam = async () => {
    if (!editingId || !draft) return
    const emails = draft.emails
      .split(',')
      .map(e => e.trim())
      .filter(Boolean)
    if (emails.length === 0) {
      toast({
        title: 'At least one address is required',
        // A crew with no address is one the crew emails silently skip, and a
        // skipped crew notices only by turning up at no house at all.
        description: 'A crew with no email address never receives its schedule.',
        variant: 'destructive',
      })
      return
    }
    setIsSaving(true)
    try {
      await api.updateCleaningTeam(editingId, {
        short_name: draft.short_name.trim(),
        accounting_name: draft.accounting_name.trim(),
        emails,
        // An empty string, NOT undefined. JSON.stringify drops an undefined
        // field, and the PATCH endpoint preserves anything the patch omits —
        // so clearing a phone number reported success and kept the old one.
        phone: draft.phone.trim(),
        color: draft.color,
        label_mode: draft.label_mode,
        active: draft.active,
      })
      await queryClient.invalidateQueries({ queryKey: ['cleaning-teams'] })
      // Each property's Cleaning card embeds this same team list in its own
      // response, so without this a crew retired here still shows as active and
      // assignable over there for the length of that query's freshness window.
      await queryClient.invalidateQueries({ queryKey: ['property-cleaning'] })
      setEditingId(null)
      setDraft(null)
      toast({ title: 'Crew saved' })
    } catch (err: unknown) {
      toast({
        title: 'Failed to save crew',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleCreateTeam = async () => {
    if (!newTeam) return
    const emails = newTeam.emails
      .split(',')
      .map(e => e.trim())
      .filter(Boolean)
    if (!TEAM_ID_PATTERN.test(newTeam.id.trim())) {
      toast({
        title: 'Invalid crew id',
        description:
          'Lowercase letters, digits, hyphens or underscores. It cannot be changed later.',
        variant: 'destructive',
      })
      return
    }
    if (emails.length === 0) {
      toast({
        title: 'At least one address is required',
        description: 'A crew with no email address never receives its schedule.',
        variant: 'destructive',
      })
      return
    }
    setIsSaving(true)
    try {
      await api.createCleaningTeam({
        id: newTeam.id.trim(),
        name: newTeam.name.trim(),
        short_name: newTeam.short_name.trim(),
        accounting_name: newTeam.accounting_name.trim(),
        emails,
        phone: newTeam.phone.trim(),
        color: newTeam.color,
        active: true,
        label_mode: newTeam.label_mode,
      })
      await queryClient.invalidateQueries({ queryKey: ['cleaning-teams'] })
      await queryClient.invalidateQueries({ queryKey: ['property-cleaning'] })
      setNewTeam(null)
      toast({ title: 'Crew added' })
    } catch (err: unknown) {
      toast({
        title: 'Failed to add crew',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveTimes = async () => {
    setIsSaving(true)
    try {
      await api.updateCleaningSettings({
        checkin_time: checkinTime,
        checkout_time: checkoutTime,
      })
      // Cleared before the refetch lands so the fresh response re-seeds these
      // inputs rather than being ignored as an unsaved edit.
      timesDirty.current = false
      await queryClient.invalidateQueries({ queryKey: ['cleaning-settings'] })
      toast({ title: 'Check-in and check-out times saved' })
    } catch (err: unknown) {
      toast({
        title: 'Failed to save times',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const isLoading = teamsQuery.isLoading || settingsQuery.isLoading
  const error = teamsQuery.error || settingsQuery.error

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cleaning crews</CardTitle>
        <CardDescription>
          The crews, what Clearing pays them under, and the global check-in and check-out times.
          Per-property rates live on each property&apos;s Cleaning card.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingSkeleton className="h-40 w-full" />
        ) : error ? (
          <ErrorState
            error={error}
            onRetry={() => {
              void teamsQuery.refetch()
              void settingsQuery.refetch()
            }}
          />
        ) : (
          <div className="space-y-5">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-textMuted">Rollout</span>
                <Badge variant={switches?.tracker_enabled ? 'default' : 'secondary'}>
                  Tracker {switches?.tracker_enabled ? 'on' : 'off'}
                </Badge>
              </div>
              <p className="text-xs text-textMuted">
                A deploy-time setting, not a toggle - it changes with a deploy of admin-api, which
                is why it is shown here as status only. With the tracker on, each crew is emailed at
                the address on its record.
              </p>
              <MutedCrewsNotice mutedCrews={settingsQuery.data?.muted_crews} />
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium text-textMuted">Default times</p>
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <label htmlFor="cleaning-checkin" className="block text-xs text-textMuted">
                    Check-in
                  </label>
                  <input
                    id="cleaning-checkin"
                    type="time"
                    value={checkinTime}
                    disabled={isSaving}
                    onChange={e => {
                      timesDirty.current = true
                      setCheckinTime(e.target.value)
                    }}
                    className="flex h-8 w-28 rounded-input border border-border bg-surface2 px-2 text-sm text-text focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 disabled:opacity-50"
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="cleaning-checkout" className="block text-xs text-textMuted">
                    Check-out
                  </label>
                  <input
                    id="cleaning-checkout"
                    type="time"
                    value={checkoutTime}
                    disabled={isSaving}
                    onChange={e => {
                      timesDirty.current = true
                      setCheckoutTime(e.target.value)
                    }}
                    className="flex h-8 w-28 rounded-input border border-border bg-surface2 px-2 text-sm text-text focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 disabled:opacity-50"
                  />
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant={timesChanged ? 'default' : 'secondary'}
                  disabled={isSaving || timesInvalid}
                  onClick={handleSaveTimes}
                >
                  Save times
                </Button>
              </div>
              <p className="text-xs text-textMuted">
                One global pair, applied to every property. A cleaning has its own scheduled time,
                which can be later than checkout.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-medium text-textMuted">Crews</p>
                {!newTeam && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={isSaving || editingId !== null}
                    onClick={() => setNewTeam(EMPTY_NEW_TEAM)}
                  >
                    Add crew
                  </Button>
                )}
              </div>

              {newTeam && (
                <div className="space-y-2 rounded-card border border-border p-3">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <TextField
                      id="new-crew-id"
                      label="Id (permanent)"
                      value={newTeam.id}
                      disabled={isSaving}
                      onChange={v => setNewTeam({ ...newTeam, id: v })}
                    />
                    <TextField
                      id="new-crew-name"
                      label="Name"
                      value={newTeam.name}
                      disabled={isSaving}
                      onChange={v => setNewTeam({ ...newTeam, name: v })}
                    />
                    <TextField
                      id="new-crew-short"
                      label="Short name"
                      value={newTeam.short_name}
                      disabled={isSaving}
                      onChange={v => setNewTeam({ ...newTeam, short_name: v })}
                    />
                    <TextField
                      id="new-crew-payee"
                      label="Clearing payee"
                      value={newTeam.accounting_name}
                      disabled={isSaving}
                      onChange={v => setNewTeam({ ...newTeam, accounting_name: v })}
                    />
                    <TextField
                      id="new-crew-emails"
                      label="Emails (comma separated)"
                      value={newTeam.emails}
                      disabled={isSaving}
                      onChange={v => setNewTeam({ ...newTeam, emails: v })}
                    />
                    <TextField
                      id="new-crew-phone"
                      label="Phone"
                      value={newTeam.phone}
                      disabled={isSaving}
                      onChange={v => setNewTeam({ ...newTeam, phone: v })}
                    />
                    <div className="space-y-1">
                      <label htmlFor="new-crew-color" className="block text-xs text-textMuted">
                        Calendar colour
                      </label>
                      <input
                        id="new-crew-color"
                        type="color"
                        value={newTeam.color}
                        disabled={isSaving}
                        onChange={e => setNewTeam({ ...newTeam, color: e.target.value })}
                        className="h-8 w-16 rounded-input border border-border bg-surface2"
                      />
                    </div>
                    <div className="space-y-1">
                      <label htmlFor="new-crew-label" className="block text-xs text-textMuted">
                        Calendar labels
                      </label>
                      <select
                        id="new-crew-label"
                        value={newTeam.label_mode}
                        disabled={isSaving}
                        onChange={e =>
                          setNewTeam({
                            ...newTeam,
                            label_mode: e.target.value as 'nickname' | 'address',
                          })
                        }
                        className="flex h-8 w-full rounded-input border border-border bg-surface2 px-2 text-sm text-text focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2"
                      >
                        <option value="nickname">Property nickname</option>
                        <option value="address">Street address</option>
                      </select>
                    </div>
                  </div>
                  <p className="text-xs text-textMuted">
                    The id cannot be changed later - every job and every property rate keys on it.
                    The Clearing payee is what the crew is paid under, which is usually not the same
                    string as the name.
                  </p>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" disabled={isSaving} onClick={handleCreateTeam}>
                      {isSaving ? 'Adding…' : 'Add crew'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={isSaving}
                      onClick={() => setNewTeam(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {teams.length === 0 && !newTeam ? (
                <p className="text-xs text-textMuted">No crews configured yet.</p>
              ) : (
                teams.map(team =>
                  editingId === team.id && draft ? (
                    <div key={team.id} className="space-y-2 rounded-card border border-border p-3">
                      <div className="grid gap-2 sm:grid-cols-2">
                        <TextField
                          id={`crew-short-${team.id}`}
                          label="Short name"
                          value={draft.short_name}
                          disabled={isSaving}
                          onChange={v => setDraft({ ...draft, short_name: v })}
                        />
                        <TextField
                          id={`crew-payee-${team.id}`}
                          label="Clearing payee"
                          value={draft.accounting_name}
                          disabled={isSaving}
                          onChange={v => setDraft({ ...draft, accounting_name: v })}
                        />
                        <TextField
                          id={`crew-emails-${team.id}`}
                          label="Emails (comma separated)"
                          value={draft.emails}
                          disabled={isSaving}
                          onChange={v => setDraft({ ...draft, emails: v })}
                        />
                        <TextField
                          id={`crew-phone-${team.id}`}
                          label="Phone"
                          value={draft.phone}
                          disabled={isSaving}
                          onChange={v => setDraft({ ...draft, phone: v })}
                        />
                        <div className="space-y-1">
                          <label
                            htmlFor={`crew-color-${team.id}`}
                            className="block text-xs text-textMuted"
                          >
                            Calendar colour
                          </label>
                          <input
                            id={`crew-color-${team.id}`}
                            type="color"
                            value={draft.color}
                            disabled={isSaving}
                            onChange={e => setDraft({ ...draft, color: e.target.value })}
                            className="h-8 w-16 rounded-input border border-border bg-surface2"
                          />
                        </div>
                        <div className="space-y-1">
                          <label
                            htmlFor={`crew-label-${team.id}`}
                            className="block text-xs text-textMuted"
                          >
                            Calendar labels
                          </label>
                          <select
                            id={`crew-label-${team.id}`}
                            value={draft.label_mode}
                            disabled={isSaving}
                            onChange={e =>
                              setDraft({
                                ...draft,
                                label_mode: e.target.value as 'nickname' | 'address',
                              })
                            }
                            className="flex h-8 w-full rounded-input border border-border bg-surface2 px-2 text-sm text-text focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2"
                          >
                            <option value="nickname">Property nickname</option>
                            <option value="address">Street address</option>
                          </select>
                        </div>
                      </div>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={draft.active}
                          disabled={isSaving}
                          onChange={e => setDraft({ ...draft, active: e.target.checked })}
                          className="h-4 w-4 rounded border-border text-mint focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2"
                        />
                        <span>
                          Active
                          <span className="block text-xs text-textMuted">
                            Unchecking retires the crew from new assignments. Crews are never
                            deleted - this one stays the payee on everything it was already paid
                            for.
                          </span>
                        </span>
                      </label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          disabled={isSaving}
                          onClick={handleSaveTeam}
                        >
                          {isSaving ? 'Saving…' : 'Save crew'}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={isSaving}
                          onClick={() => {
                            setEditingId(null)
                            setDraft(null)
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      key={team.id}
                      className="flex flex-wrap items-center gap-2 rounded-card border border-border p-3"
                    >
                      <span
                        aria-hidden="true"
                        className="h-3 w-3 shrink-0 rounded-full border border-border"
                        style={{ backgroundColor: team.color }}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-text">
                          {team.name}
                          {!team.active && (
                            <Badge variant="secondary" className="ml-2 align-middle">
                              Retired
                            </Badge>
                          )}
                        </p>
                        <p className="truncate text-xs text-textMuted">
                          Paid as {team.accounting_name} · {team.emails.join(', ') || 'no email'}
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        // Disabled while another crew's save is in flight: that
                        // save clears the editor when it lands, so an edit
                        // started underneath it would be discarded silently.
                        disabled={isSaving}
                        onClick={() => startEdit(team)}
                      >
                        Edit
                      </Button>
                    </div>
                  )
                )
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function TextField({
  id,
  label,
  value,
  disabled,
  onChange,
}: {
  id: string
  label: string
  value: string
  disabled?: boolean
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs text-textMuted">
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        disabled={disabled}
        onChange={e => onChange(e.target.value)}
        className="flex h-8 w-full rounded-input border border-border bg-surface2 px-2 text-sm text-text focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      />
    </div>
  )
}
